interface InkPreviewData {
  type: 'ink';
  viewBox: string;
  paths: string[];
}

interface TextPreviewData {
  type: 'text';
  text: string;
  fontFamily: string;
}

export type XfdfPreviewData = InkPreviewData | TextPreviewData | null;

export function parseXfdfPreview(xfdf: string): XfdfPreviewData {
  const parser = new DOMParser();
  const doc = parser.parseFromString(xfdf, 'text/xml');

  const ink = doc.querySelector('ink');
  if (ink) {
    return parseInkPreview(ink);
  }

  const freetext = doc.querySelector('freetext');
  if (freetext) {
    return parseFreetextPreview(freetext);
  }

  return null;
}

function parseInkPreview(ink: Element): InkPreviewData | null {
  const rect = ink.getAttribute('rect');
  if (!rect) {
    return null;
  }

  const [x1, y1, x2, y2] = rect.split(',').map(Number);
  const width = x2 - x1;
  const height = y2 - y1;
  if (width <= 0 || height <= 0) {
    return null;
  }

  const gestures = ink.querySelectorAll('gesture');
  const paths: string[] = [];

  gestures.forEach((gesture) => {
    const raw = gesture.textContent?.trim();
    if (!raw) {
      return;
    }

    const points = raw.split(';').map((pair) => {
      const [px, py] = pair.split(',').map(Number);
      return { x: px - x1, y: height - (py - y1) };
    });

    if (points.length < 2) {
      return;
    }

    const d = points
      .map(
        (p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`
      )
      .join(' ');
    paths.push(d);
  });

  if (paths.length === 0) {
    return null;
  }

  return {
    type: 'ink',
    viewBox: `0 0 ${width.toFixed(2)} ${height.toFixed(2)}`,
    paths,
  };
}

function parseFreetextPreview(freetext: Element): TextPreviewData | null {
  const contentsElem = freetext.querySelector('contents');
  const text = contentsElem?.textContent?.trim();
  if (!text) {
    return null;
  }

  let fontFamily = 'Dancing Script';
  const style = freetext.getAttribute('style');
  if (style) {
    const fontMatch = style.match(/font-family:\s*([^;]+)/i);
    if (fontMatch) {
      fontFamily = fontMatch[1].trim().replace(/['"]/g, '');
    }
  }

  const defaultStyle =
    freetext.querySelector('defaultstyle')?.textContent ?? '';
  if (defaultStyle) {
    const dsFontMatch = defaultStyle.match(/font-family:\s*([^;]+)/i);
    if (dsFontMatch) {
      fontFamily = dsFontMatch[1].trim().replace(/['"]/g, '');
    }
  }

  return { type: 'text', text, fontFamily };
}
